<!--
  Simple table for facility state objects
    First column: Text column
    Second column: Boolean column
    Third column: Text column
    Fourth column: Documentations
-->

<template>
  <div class="state-table">
    <!-- Readonly Table -->
    <v-data-table
        v-if="loaded && readOnly"
        hide-default-footer
        hide-default-header
        mobile-breakpoint="700"
    >
      <template v-slot:header>
        <thead>
        <tr class="d-none d-md-table-row">
          <th v-for="h in header" :key="h" class="d-none d-sm-table-cell">
            <h6 class="mb-0">{{ h }}</h6>
          </th>
        </tr>
        </thead>
      </template>
      <template v-slot:body>

        <!-- Mobile Table body -->
        <v-row class="d-md-none border-bottom-line" v-for="i in fields[0].values.length" :key="i">
          <v-col>
            <v-row v-for="(h, j) in header" :key="j">
              <v-col cols="6">
                <p class="mb-0 font-weight-bold">{{ h }}</p>
              </v-col>
              <v-col cols="6" class="text-right" v-if="j === 0">
                {{ fields[0].values[i - 1]}}
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 1">
                <v-checkbox disabled class="mt-0 right-checkbox" v-model="data[fields[1].values[i - 1]]"/>
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 2">
                {{ data[fields[2].values[i - 1]] }}
              </v-col>
              <v-col cols="12" class="text-right" v-else-if="j === 3">
                <v-carousel
                    height="300"
                    v-show="data[fields[3].values[i - 1]].length > 0"
                    :class="getCarouselHeight(data[fields[3].values[i - 1]].length, true)">
                  <v-carousel-item
                      v-for="docu in data[fields[3].values[i - 1]]"
                      :key="docu.id"
                  >
                    <div class="pdf-preview-wrapper-mobile" v-if="docu.fileType === 'application/pdf'">
                      <div class="pdf-preview-mobile" >
                        <pdf
                            ref="pdf"
                            :src="docu.src"
                            class="pdf-mobile"
                            @click.native="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                        >
                        </pdf>
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <div class="img-preview-wrapper-mobile" v-else>
                      <div class="img-preview-mobile">
                        <v-img
                            :src="docu.src"
                            width="200"
                            @click="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                        />
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>
                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- NOT Mobile Table body -->
        <tbody>
        <tr v-for="i in fields[0].values.length" :key="i" class="d-none d-md-table-row">
          <td class="d-table-row d-sm-table-cell">{{ fields[0].values[i - 1]}}</td>
          <td class="d-table-row d-sm-table-cell">
            <v-checkbox
                v-model="data[fields[1].values[i - 1]]"
                disabled
            />
          </td>
          <td class="d-table-row d-sm-table-cell">{{ data[fields[2].values[i - 1]] }}</td>
          <td class="custom-column py-3">
            <v-carousel
                height="230"
                v-show="data[fields[3].values[i - 1]].length > 0"
                :class="getCarouselHeight(data[fields[3].values[i - 1]].length, false)">
              <v-carousel-item
                  v-for="docu in data[fields[3].values[i - 1]]"
                  :key="docu.id"
              >
                <div class="pdf-preview-wrapper" v-if="docu.fileType === 'application/pdf'">
                  <div class="pdf-preview" >
                    <pdf
                        ref="pdf"
                        :src="docu.src"
                        class="pdf-mobile"
                        @click.native="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                    >
                    </pdf>
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>

                <div class="img-preview-wrapper" v-else>
                  <div class="img-preview">
                    <v-img
                        :src="docu.src"
                        width="150"
                        @click="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                    />
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>
              </v-carousel-item>
            </v-carousel>
          </td>
        </tr>
        </tbody>
      </template>
    </v-data-table>

    <!-- Table with ability to fill in data -->
    <v-data-table
        v-else-if="loaded"
        hide-default-footer
        hide-default-header
        mobile-breakpoint="700"
    >
      <template v-slot:header>
        <thead>
        <tr class="d-none d-md-table-row">
          <th v-for="h in header" :key="h" class="d-none d-sm-table-cell">
            <h6 class="mb-0">{{ h }}</h6>
          </th>
        </tr>
        </thead>
      </template>
      <template v-slot:body>

        <!-- Mobile Table body -->
        <v-row class="d-md-none border-bottom-line" v-for="i in fields[0].values.length" :key="i">
          <v-col>
            <v-row v-for="(h, j) in header" :key="j">
              <v-col cols="6" align-self="end">
                <p class="mb-0 font-weight-bold">{{ h }}</p>
              </v-col>
              <v-col cols="6" class="text-right" v-if="j === 0">
                {{ fields[0].values[i - 1]}}
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 1">
                <v-checkbox
                    class="mt-0 right-checkbox"
                    v-model="data[fields[1].values[i - 1]]"
                    hide-details
                />
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 2">
                <v-text-field
                    class="pt-0"
                    v-model="data[fields[2].values[i - 1]]"
                    :label="$t('common:comment')"
                    hide-details
                />
              </v-col>
              <v-col cols="6" class="text-right" v-else-if="j === 3">
                <v-btn icon color="primary" @click="openMobileCamera(i)">
                  <v-icon>add_a_photo</v-icon>
                </v-btn>
                <DocumentationUploadForm
                    :ref="'docuUploadForm_' + i"
                    color="primary"
                    noBtnText
                    @postFile="pushDocumentToState($event, i, fields, data)"
                    :presetDocuName="fields[0].values[i - 1] + '_' + (data[fields[3].values[i - 1]].filter(e => !e.toDelete).length + 1)"
                />
              </v-col>
              <v-col cols="12" class="text-right" v-else-if="j === 4">
                <v-carousel
                    height="300"
                    v-show="data[fields[3].values[i - 1]].filter(e => !e.toDelete).length > 0"
                    :class="getCarouselHeight(data[fields[3].values[i - 1]].length, true)">
                  <v-carousel-item
                      v-for="docu in data[fields[3].values[i - 1]].filter(e => !e.toDelete)"
                      :key="docu.id"
                  >
                    <div class="pdf-preview-wrapper-mobile" v-if="docu.fileType === 'application/pdf'">
                      <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, i, 'dialogDeleteDocumentation_Mobile_' + i)">
                        <v-icon>
                          close
                        </v-icon>
                      </v-btn>
                      <div class="pdf-preview-mobile" >
                        <pdf
                            ref="pdf"
                            :src="docu.src"
                            class="pdf-mobile"
                            @click.native="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                        >
                        </pdf>
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <div class="img-preview-wrapper-mobile" v-else>
                      <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, i, 'dialogDeleteDocumentation_Mobile_' + i)">
                        <v-icon>
                          close
                        </v-icon>
                      </v-btn>
                      <div class="img-preview-mobile">
                        <v-img
                            :src="docu.src"
                            width="200"
                            @click="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                        />
                        <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                      </div>
                    </div>

                    <TwoButtonDialog
                        :ref="'dialogDeleteDocumentation_Mobile_' + i"
                        hideButton
                        :btn-text="$t('buttons:delete')" color="error"
                        :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-1')})"
                        :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-2')})"
                        :button1-text="$t('buttons:cancel')"
                        :button2-text="$t('buttons:delete')"
                        @clickedButton1="$refs['dialogDeleteDocumentation_' + i][0].showDialog = false"
                        @clickedButton2="deleteDocumentationConfirmed('dialogDeleteDocumentation_Mobile_' + i)"
                    />

                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- NOT Mobile Table body -->
        <tbody>
        <tr v-for="i in fields[0].values.length" :key="i" class="d-none d-md-table-row">
          <td class="d-table-row d-sm-table-cell">{{ fields[0].values[i - 1]}}</td>
          <td class="d-table-row d-sm-table-cell">
            <v-checkbox
                v-model="data[fields[1].values[i - 1]]"
            />
          </td>
          <td class="d-table-row d-sm-table-cell">
            <v-text-field
                class="pt-0"
                v-model="data[fields[2].values[i - 1]]"
                :label="$t('common:comment')"
                hide-details
            />
          </td>
          <td>
            <CameraUploadForm
                :ref="'cameraUploadForm_' + i"
                color="primary"
                noBtnText
                @postFile="pushDocumentToState($event, i, fields, data)"
                :presetDocuName="fields[0].values[i - 1] + '_' + (data[fields[3].values[i - 1]].filter(e => !e.toDelete).length + 1)"
            />
            <DocumentationUploadForm
                :ref="'docuUploadForm_' + i"
                color="primary"
                noBtnText
                @postFile="pushDocumentToState($event, i, fields, data)"
                :presetDocuName="fields[0].values[i - 1] + '_' + (data[fields[3].values[i - 1]].filter(e => !e.toDelete).length + 1)"
            />
          </td>
          <td class="custom-column py-3">
            <v-carousel
                height="230"
                v-show="data[fields[3].values[i - 1]].filter(e => !e.toDelete).length > 0"
                :class="getCarouselHeight(data[fields[3].values[i - 1]].length, false)">
              <v-carousel-item
                  v-for="docu in data[fields[3].values[i - 1]].filter(e => !e.toDelete)"
                  :key="docu.id"
              >
                <div class="pdf-preview-wrapper" v-if="docu.fileType === 'application/pdf'">
                  <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, i, 'dialogDeleteDocumentation_' + i)">
                    <v-icon>
                      close
                    </v-icon>
                  </v-btn>
                  <div class="pdf-preview" >
                    <pdf
                        ref="pdf"
                        :src="docu.src"
                        class="pdf-mobile"
                        @click.native="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                    >
                    </pdf>
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>

                <div class="img-preview-wrapper" v-else>
                  <v-btn icon color="scaleRed" @click="deleteDocumentation(docu.localId, i, 'dialogDeleteDocumentation_' + i)">
                    <v-icon>
                      close
                    </v-icon>
                  </v-btn>
                  <div class="img-preview">
                    <v-img
                        :src="docu.src"
                        width="150"
                        @click="viewDocumentation(data[fields[3].values[i - 1]], docu)"
                    />
                    <p class="mb-0 greyMedium--text text-center">{{ docu.formattedSavedOn }}</p>
                  </div>
                </div>

                <TwoButtonDialog
                    :ref="'dialogDeleteDocumentation_' + i"
                    hideButton
                    :btn-text="$t('buttons:delete')" color="error"
                    :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-1')})"
                    :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/public:qrcode.onboarding.dialogs.documentation-2')})"
                    :button1-text="$t('buttons:cancel')"
                    :button2-text="$t('buttons:delete')"
                    @clickedButton1="$refs['dialogDeleteDocumentation_' + i][0].showDialog = false"
                    @clickedButton2="deleteDocumentationConfirmed('dialogDeleteDocumentation_' + i)"
                />

              </v-carousel-item>
            </v-carousel>
          </td>
        </tr>
        </tbody>
      </template>
    </v-data-table>

    <!-- Additional Components -->
    <DocumentViewer ref="documentViewer" offline/>

    <MobileCameraUploadForm
        ref="mobileCameraUploadForm"
        color="primary"
        noBtnText
        @postPhotos="postPhotos($event, fields, data)"
        offline-mode
    />
  </div>
</template>
<script>
/** @TODO refactor the code
 40:75  error  Unexpected mutation of "data" prop  vue/no-mutating-props
 90:26  error  Unexpected mutation of "data" prop  vue/no-mutating-props
 166:30  error  Unexpected mutation of "data" prop  vue/no-mutating-props
 173:30  error  Unexpected mutation of "data" prop  vue/no-mutating-props
 256:26  error  Unexpected mutation of "data" prop  vue/no-mutating-props
 262:26  error  Unexpected mutation of "data" prop  vue/no-mutating-props
 */
import CameraUploadForm from "@/modules/core/components/CameraUploadForm";
import DocumentationUploadForm from "@/modules/core/components/DocumentationUploadForm";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DocumentationService from "@/modules/core/services/documentation-service";
import Vue from "vue";
import Utils from "@/modules/core/services/utils";
import Documentation from "@/modules/book/data/documentation";
import indexedDBService from "@/modules/core/services/indexedDB-service";
import MobileCameraUploadForm from "@/modules/core/components/MobileCameraUploadForm";
import DateUtils from "@/modules/core/services/date-utils";

export default {
  name: "StateTable",
  props: {
    data: Object, // state object to display
    header: Array, // header translation for table
    fields: Array, // table fields
    readOnly: Boolean, // if table is readonly or if values can be inserted
    facilityState: Object
  },
  components: {
    MobileCameraUploadForm,
    TwoButtonDialog,
    DocumentationUploadForm,
    CameraUploadForm,
    DocumentViewer: () => import('@/modules/core/components/DocumentViewer/DocumentViewer'),
    pdf: () => import('vue-pdf')
  },
  data() {
    return {
      loaded: false,

      // delete object which includes local docu id and all documentations from this state
      deleteDocu: {
        docuId: null,
        stateDocuments: null
      },
    }
  },
  watch: {
    // if data load is finished
    fields() {
      this.loadDocumentsWithChecks();
    }
  },
  methods: {
    /**
     * check if field this.fields & this.data is loaded (comming from parent) and
     * load corresponding documents if available.
     *
     * check on this.loaded guarantees that loading is only done once
     *
     * is either triggered by watch fields() on eager loading (in v-expansion-panel-content) or
     * manually called from parent with this.$refs.stateTableX.checkDataLoaded() when used lazy loading
     */
    loadDocumentsWithChecks() {
      if (this.loaded) {
        return;  // documents already loaded - nothing to do
      }
      if (this.fields && this.data) {
        this.loaded = true;

        // get document file to display it
        // this.fields[3].values ... documentation fields like carRideComfortDocs
        for (let docField of this.fields[3].values) {
          if (this.data[docField].length > 0) {
            // loop through all documentations of this state
            // e.g. this.data[carRideComfortDocs]
            for (let doc of this.data[docField]) {
              doc.formattedSavedOn = DateUtils.parseDateTimeForDisplay(doc.savedOn);
              doc.localId = Utils.generateUUIDv4();
              DocumentationService.getDocByTypeAndIdForPublic(
                  doc.id,
                  doc.fileType,
                  doc.documentType,
                  doc.facilityId,
                  this.$route.params.code,
                  (res) => {
                    Vue.set(doc, "src", res);
                  }
              )
            }
          }
        }
      }
    },

    viewDocumentation(documents, data) {
      this.$refs.documentViewer.initViewer(documents, data.fileName)
    },

    // if user wants to delete a documentation
    // puffer the localId of the documentation and all documentations of this state like carRideComfort
    // stateIndex is the index of the state like carRideComfort
    deleteDocumentation(docuLocalId, stateIndex, ref) {
      this.deleteDocu.docuId = docuLocalId;
      this.deleteDocu.stateDocuments = this.data[this.fields[3].values[stateIndex - 1]];

      this.$refs[ref][0].showDialog = true;
    },

    deleteDocumentationConfirmed(ref) {
      this.$refs[ref][0].setButtonSettings(false, false, false, false);
      this.$refs[ref][0].showDialog = false;

      // tag document, which should be deleted, with toDelete = true
      for (let i = 0; i < this.deleteDocu.stateDocuments.length; i++) {
        if (this.deleteDocu.stateDocuments[i].localId === this.deleteDocu.docuId) {
          Vue.set(this.deleteDocu.stateDocuments[i], 'toDelete', true);
          Vue.set(this.deleteDocu.stateDocuments[i], 'toSave', false);
        }
      }

      this.deleteDocu.docuId = null;
      this.deleteDocu.stateDocuments = null;

      indexedDBService.saveFacilityState(this.facilityState)
        .then(() => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:documentation')}));
        })
        .catch(() => {
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:documentation')}));
        })
    },

    /**
     * pushes new document to state
     * we have to pass fields and data again here, because without it
     * we couldn't access it inside the eventListener
     *
     * @param args induces file and document infos (e.g. name, startDate, endDate)
     * @param stateIndex index of the state like carRideComfort
     * @param fields all header fields, see state-table-utils for more explanation
     * @param data state
     */
    pushDocumentToState(args, stateIndex, fields, data) {
      let reader = new FileReader();

      // have to define needed vars here, because otherwise
      // we can't access e.g. args.documentation.start inside
      // the load eventListener
      const startDate = args.documentation.start;
      const endDate = args.documentation.end;
      const name = args.documentation.name;
      const file = args.file;

      // add event listener to reader, so when readAsDataURL is finished
      // the code inside event listener will be executed and we can read
      // the source of the document via reader.result
      reader.addEventListener("load", function () {
        let document = new Documentation();

        if (startDate == null) {
          document.start = '';
        } else {
          document.start = startDate;
        }

        if (endDate == null) {
          document.end = '';
        } else {
          document.end = endDate;
        }

        document.src = reader.result;
        document.name = name;
        // set the type of the document, e.g. CAR_RIDE_COMFORT
        document.type = fields[4].values[stateIndex - 1];
        document.file = file;
        document.toSave = true;
        document.fileType = file.type;

        // define a unique id, so the delete handling will be easier
        document.localId = Utils.generateUUIDv4();

        // if, e.g. carRideComfortDocs, is not declared
        // define an empty array to it
        if(!data[fields[3].values[stateIndex - 1]]) {
          data[fields[3].values[stateIndex - 1]] = [];
        }

        // push new document to the document field of the state
        // e.g. carRideComfortDocs.push(...)
        data[fields[3].values[stateIndex - 1]].push(document);
      }, false);

      // if there is a file, read the content of the file
      // to display the document without the backend
      if (file) {
        reader.readAsDataURL(file);
      }

      // it is not needed to trigger safeFacilityState here manually
      // because for state documentation posts, the watcher in QRCodeFacilityOnboarding
      // will be called
    },

    // Only mobile
      postPhotos(response, fields, data) {
      let existingDocuments = data[fields[3].values[response.rowToPut - 1]];
      let newDocuments = response.documents;

      // if, e.g. carRideComfortDocs, is not declared
      if(!existingDocuments) {
        existingDocuments = newDocuments;
      } else {
        for (let i = 0; i < newDocuments.length; i++) {
          // push document to the document field of the state
          // e.g. carRideComfortDocs.push(...)
          existingDocuments.push(newDocuments[i]);
        }
      }
    },

    getCarouselHeight(elementCount, isMobile) {
      if (isMobile) {
        if (elementCount <= 9) {
          return 'carousel-control-small--mobile'
        } else {
          return 'carousel-control-normal--mobile'
        }
      } else {
        if (elementCount <= 6) {
          return 'carousel-control-small'
        } else if (elementCount > 6 && elementCount <= 12) {
          return 'carousel-control-normal'
        } else if (elementCount > 12) {
          return 'carousel-control-big'
        }
      }
    },

    openMobileCamera(index) {
      let presetDocuName = this.fields[0].values[index - 1] + '_' + (this.data[this.fields[3].values[index - 1]].filter(e => !e.toDelete).length + 1);
      let documentationType = this.fields[4].values[index - 1];
      this.$refs.mobileCameraUploadForm.openCameraDialog(presetDocuName, documentationType, index)
    }
  }
}
</script>
<style lang="scss">
.state-table {
  .right-checkbox {
    .v-input__control {
      .v-input__slot {
        justify-content: flex-end;
        margin-bottom: 0;
      }
    }
  }
  .border-bottom-line {
    box-shadow: 0 2px 1px 0 #e8eafc, 0 1px 1px -2px #b2b2b21a, 0 1px 1px 0 #9a9a9a1a;
  }

  /**
   ****** Vuetify Carousel CSS changes ******
   */
  // left btn smaller
  .v-window__prev {
    .v-btn {
      height: 24px !important;
      width: 24px !important;

      .v-icon {
        font-size: 24px !important;
      }
    }
  }

  // right btn smaller
  .v-window__next {
    .v-btn {
      height: 24px !important;
      width: 24px !important;

      .v-icon {
        font-size: 24px !important;
      }
    }
  }

  // Bottom control panel smaller
  // if elements are under 6
  .carousel-control-small {
    .v-carousel__controls {
      height: 30px!important;
    }
  }
  // if there are more than 6 elements
  .carousel-control-normal {
    .v-carousel__controls {
      height: 55px!important;
    }
  }

  // if there are more than 12 elements
  .carousel-control-big {
    .v-carousel__controls {
      height: 75px!important;
    }
  }

  /**
   ****** Normal screen sizes and up ******
   */
  .custom-column {
    width: 300px;
  }

  .pdf-preview-wrapper {
    position: relative;
    width: 100px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .pdf-preview {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 100px;
      cursor: pointer;

      .pdf-mobile {
        width: auto;
      }
    }
  }

  .img-preview-wrapper {
    position: relative;
    width: 150px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .img-preview {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 150px;
      cursor: pointer;
    }
  }

  /**
   ****** MOBILE ******
   */
  .pdf-preview-wrapper-mobile {
    position: relative;
    width: 150px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .pdf-preview-mobile {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 150px;
      cursor: pointer;

      .pdf-mobile {
        width: auto;
      }
    }
  }

  .img-preview-wrapper-mobile {
    position: relative;
    width: 200px;
    margin-left: auto;
    margin-right: auto;

    button {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .img-preview-mobile {
      border: 1px solid #ddd; /* Gray border */
      border-radius: 4px; /* Rounded border */
      padding: 5px; /* Some padding */
      width: 200px;
      cursor: pointer;
    }
  }

  // Bottom carousel control panel smaller
  // if elements are under 6
  .carousel-control-small--mobile {
    .v-carousel__controls {
      height: 30px!important;
    }
  }
  // if there are more than 6 elements
  .carousel-control-normal--mobile {
    .v-carousel__controls {
      height: 55px!important;
    }
  }

  // if there are more than 12 elements
  .carousel-control-big--mobile {
    .v-carousel__controls {
      height: 55px!important;
    }
  }
}
</style>